import { motion } from "framer-motion";
import {
    Briefcase,
    Settings2,
    Users,
    FolderKanban,
    MessagesSquare,
    BrainCircuit,
} from "lucide-react";

const functions = [
    {
        icon: Briefcase,
        label: "Sales",
        desc: "Pipeline, deals, quotes & revenue forecasts — one connected funnel.",
    },
    {
        icon: Settings2,
        label: "Finance",
        desc: "Invoicing, expenses, books & reports — one connected system.",
    },
    {
        icon: Users,
        label: "HR",
        desc: "People, attendance, payroll inputs & performance — all in one record.",
    },
    {
        icon: FolderKanban,
        label: "Projects",
        desc: "Tasks, sprints & roadmaps tied directly to revenue and goals.",
    },
    {
        icon: MessagesSquare,
        label: "Communication",
        desc: "Chat, docs and decisions threaded to the work they belong to.",
    },
    {
        icon: BrainCircuit,
        label: "Intelligence",
        desc: "An AI layer that reads, reasons and acts across every module.",
    },
];

export const CoreFunctions = () => {
    return (
        <section
            id="product"
            className="relative py-24 sm:py-32"
            data-testid="core-functions-section"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-14">
                    <div className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4 font-code">
                            // 6 pillars · 1 operating system
                        </p>
                        <h2 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
                            One workspace for every
                            <br />
                            <span className="text-zinc-500">
                                function that runs your company.
                            </span>
                        </h2>
                    </div>
                    <p className="lg:col-span-5 text-zinc-400 text-base sm:text-lg leading-relaxed font-light">
                        ApexBrain unifies the six pillars of a modern organisation —
                        no bridges, no syncs, no duplicated data. Every decision
                        moves with full context, every team works on the same truth.
                    </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/[0.06] rounded-2xl overflow-hidden border border-white/[0.06]">
                    {functions.map((f, i) => (
                        <motion.div
                            key={f.label}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                            className="group relative bg-obsidian-200 hover:bg-obsidian-100 transition-colors p-6 sm:p-7"
                            data-testid={`core-function-${f.label.toLowerCase()}`}
                        >
                            <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-5 group-hover:bg-electric/10 group-hover:border-electric/30 transition">
                                <f.icon className="w-4 h-4 text-zinc-300 group-hover:text-electric transition" />
                            </div>
                            <div className="font-outfit text-base font-semibold text-white">
                                {f.label}
                            </div>
                            <div className="text-xs text-zinc-500 mt-1.5 leading-relaxed">
                                {f.desc}
                            </div>
                            <div className="absolute top-3 right-3 text-[10px] tabular-nums font-mono text-zinc-700">
                                0{i + 1}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
